// ============================================================
// PortalModuleDetail — the per-module workspace inside a portal.
//
// Sections: Module Purpose, Module Status, Saved Data Area, Actions,
// Receipts, Deferred Integrations, Next Steps. Core modules show their
// module-scoped actions / notes / receipts (real, persisted). Inherited
// modules (Communications / Team / Daily Meetings) deep-link into the
// existing V2 spine with an honest status — this surface attaches, it does
// not rebuild them. Deferred modules are labelled plainly. Presentational +
// SSR-safe; the route wrapper owns the hook + mutations.
// ============================================================

import { useState } from 'react'
import CommandCenterShell from '../../shell/CommandCenterShell'
import { CC_INK, CC_MONO } from '../../shell'
import {
  moduleCatalogEntry, OLIVIUM_ACCENT,
  type PortalDetail as PortalDetailData, type ActionPriority,
} from './portalFactory'
import {
  PORTAL_HONESTY, ModuleStatusBadge, SectionCard, StateBlock, BackendNotReadyBanner,
  moduleStatusMeta, chipBtn, primaryBtn, inputStyle,
} from './PortalKit'

type MutationResult = void | Promise<boolean>

export interface PortalModuleDetailProps {
  detail: PortalDetailData | null
  moduleKey: string
  loading: boolean
  error: string | null
  authRequired: boolean
  notFound: boolean
  /** Honest surface for a rejected mutation (from the route wrapper). */
  mutationError?: string | null
  onHome?: () => void
  onBackCommandCenter?: () => void
  onOpenPortal?: () => void
  onOpenRegistry?: () => void
  onOpenLink?: (url: string) => void
  onAttachModule?: (moduleKey: string) => void
  onCreateAction?: (input: { title: string; priority: ActionPriority; module_key: string }) => MutationResult
  onCreateNote?: (body: string, module_key: string) => MutationResult
  onCreateReceipt?: (summary: string, module_key: string) => MutationResult
}

function InlineAdd({ placeholder, cta, onAdd }: { placeholder: string; cta: string; onAdd: (v: string) => void | Promise<boolean> }) {
  const [v, setV] = useState('')
  // Clear only after a real save — a rejected write keeps the text.
  const submit = async () => { if (!v.trim()) return; const ok = await onAdd(v.trim()); if (ok !== false) setV('') }
  return (
    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
      <input style={inputStyle} value={v} placeholder={placeholder} onChange={(e) => setV(e.target.value)} />
      <button type="button" style={chipBtn} onClick={submit}>{cta}</button>
    </div>
  )
}

export default function PortalModuleDetail(props: PortalModuleDetailProps) {
  const { detail, moduleKey, loading, error, authRequired, notFound, onHome, onBackCommandCenter, onOpenPortal, onOpenRegistry, onOpenLink } = props
  const catalog = moduleCatalogEntry(moduleKey)
  const backendNotReady = !!error && /not migrated/i.test(error)

  if (!detail) {
    return (
      <CommandCenterShell
        onHome={onHome} onBackCommandCenter={onBackCommandCenter}
        parentBack={{ label: '← Portal', onClick: onOpenPortal }}
        breadcrumb={['OLIVIUM', 'PORTAL FACTORY', 'MODULE']}
        title={catalog?.label ?? 'Module'} titleAccent={OLIVIUM_ACCENT} honestyBadge={PORTAL_HONESTY}
      >
        {backendNotReady && <BackendNotReadyBanner message={error!} />}
        {loading && <StateBlock kind="loading" title="Loading module…" />}
        {!loading && authRequired && <StateBlock kind="auth" title="Sign in to open this module" />}
        {!loading && notFound && <StateBlock kind="empty" title="Portal not found" body="This module's portal could not be loaded." />}
        {!loading && !authRequired && !notFound && error && !backendNotReady && <StateBlock kind="error" title="Could not open module" body={error} />}
        <div style={{ marginTop: 14 }}><button type="button" style={chipBtn} onClick={onOpenRegistry}>← Registry</button></div>
      </CommandCenterShell>
    )
  }

  const attached = detail.modules.find((m) => m.module_key === moduleKey) || null
  const label = attached?.module_label ?? catalog?.label ?? moduleKey
  const status = attached?.module_status ?? catalog?.status ?? 'TEMPLATE'
  const linkTarget = attached?.link_target ?? catalog?.linkTarget ?? null
  const isInherited = catalog?.category === 'inherited'
  const isFuture = catalog?.category === 'future'
  const isDeferred = status === 'BACKEND_DEFERRED' || status === 'INTEGRATION_DEFERRED' || status === 'REALTIME_DEFERRED'

  const scopedActions = detail.actions.filter((a) => a.module_key === moduleKey)
  const scopedNotes = detail.notes.filter((n) => n.module_key === moduleKey)
  const scopedReceipts = detail.receipts.filter((r) => r.module_key === moduleKey)

  return (
    <CommandCenterShell
      onHome={onHome} onBackCommandCenter={onBackCommandCenter}
      parentBack={{ label: '← Portal', onClick: onOpenPortal }}
      breadcrumb={['OLIVIUM', detail.portal.project_slug.toUpperCase(), (label || moduleKey).toUpperCase()]}
      title={label} titleAccent={OLIVIUM_ACCENT}
      statusBadge={<span style={{ marginLeft: 8 }}><ModuleStatusBadge status={status} /></span>}
      honestyBadge={PORTAL_HONESTY}
    >
      <div>
        {/* Honest surface for a rejected mutation — never a silent no-op. */}
        {props.mutationError && <div style={{ marginBottom: 14 }}><StateBlock kind="error" title="Last action failed" body={props.mutationError} /></div>}

        {/* Module Purpose */}
        <SectionCard title="Module Purpose">
          <p style={{ margin: 0, fontSize: 13, color: CC_INK.secondary, lineHeight: 1.6 }}>{catalog?.purpose ?? 'Custom module.'}</p>
          {catalog?.note && <p style={{ margin: '8px 0 0', fontSize: 11, color: CC_INK.faint, fontFamily: CC_MONO }}>{catalog.note}</p>}
        </SectionCard>

        {/* Module Status */}
        <SectionCard title="Module Status" right={<ModuleStatusBadge status={status} />}>
          {!attached ? (
            <div>
              <StateBlock kind="empty" title="Not attached to this portal yet" body="Attach this module to start tracking it here." />
              <div style={{ marginTop: 10 }}><button type="button" style={primaryBtn} onClick={() => props.onAttachModule?.(moduleKey)}>{`+ Attach ${label}`}</button></div>
            </div>
          ) : (
            <div style={{ fontSize: 11, color: CC_INK.muted, fontFamily: CC_MONO }}>
              {moduleStatusMeta(status).label} · attached {attached.created_at.slice(0, 10)} · {attached.enabled ? 'enabled' : 'disabled'}
            </div>
          )}
        </SectionCard>

        {/* Saved Data Area */}
        <SectionCard title="Saved Data Area">
          {isInherited && linkTarget ? (
            <div>
              <p style={{ margin: '0 0 10px', fontSize: 12, color: CC_INK.secondary, lineHeight: 1.5 }}>
                This module <strong>attaches the existing V2 spine</strong> rather than duplicating it. Project-scoping of the
                underlying channels / roster / meetings is a later phase; today it links to the shared surface with an honest status.
              </p>
              <button type="button" style={primaryBtn} onClick={() => onOpenLink?.(linkTarget)}>{`Open ${label} surface`}</button>
            </div>
          ) : isFuture ? (
            <StateBlock kind="empty" title={`${label} is deferred`} body={catalog?.note ?? 'This module is not built in v1.'} />
          ) : (
            <p style={{ margin: 0, fontSize: 12, color: CC_INK.muted, lineHeight: 1.5 }}>
              Module-scoped actions, notes, and receipts below are saved against this portal with this module's key —
              real records, never fabricated.
            </p>
          )}
        </SectionCard>

        {/* Actions (module-scoped; only for non-deferred modules) */}
        {!isDeferred && (
          <SectionCard title="Actions" right={<span style={{ fontSize: 10, color: CC_INK.faint, fontFamily: CC_MONO }}>{scopedActions.length}</span>}>
            {scopedActions.length === 0 && <StateBlock kind="empty" title="No module actions yet" body="Add one below." />}
            {scopedActions.map((a) => (
              <div key={a.id} style={{ display: 'flex', gap: 10, padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                <span style={{ fontSize: 9, fontFamily: CC_MONO, color: CC_INK.faint, minWidth: 84 }}>{a.status.replace('_', ' ').toUpperCase()}</span>
                <span style={{ flex: 1, fontSize: 12.5, color: CC_INK.primary }}>{a.title}</span>
              </div>
            ))}
            {attached && <InlineAdd placeholder="New module action…" cta="+ Action" onAdd={(v) => props.onCreateAction?.({ title: v, priority: 'normal', module_key: moduleKey })} />}
          </SectionCard>
        )}

        {/* Notes (module-scoped) */}
        {!isDeferred && (
          <SectionCard title="Notes" right={<span style={{ fontSize: 10, color: CC_INK.faint, fontFamily: CC_MONO }}>{scopedNotes.length}</span>}>
            {scopedNotes.length === 0 && <StateBlock kind="empty" title="No module notes yet" />}
            {scopedNotes.map((n) => (
              <div key={n.id} style={{ padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.05)', fontSize: 12, color: CC_INK.secondary }}>{n.body}</div>
            ))}
            {attached && <InlineAdd placeholder="Add a module note…" cta="+ Note" onAdd={(v) => props.onCreateNote?.(v, moduleKey)} />}
          </SectionCard>
        )}

        {/* Receipts (module-scoped) */}
        <SectionCard title="Receipts" right={<span style={{ fontSize: 10, color: CC_INK.faint, fontFamily: CC_MONO }}>{scopedReceipts.length}</span>}>
          {scopedReceipts.length === 0 && <StateBlock kind="empty" title="No module receipts yet" body="Receipts are written as you work this module." />}
          {scopedReceipts.slice(0, 10).map((r) => (
            <div key={r.id} style={{ display: 'flex', gap: 10, padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
              <span style={{ fontSize: 9, fontFamily: CC_MONO, color: '#059669', minWidth: 120 }}>{r.receipt_type}</span>
              <span style={{ flex: 1, fontSize: 11.5, color: CC_INK.secondary }}>{r.summary}</span>
            </div>
          ))}
          {attached && !isDeferred && <InlineAdd placeholder="Author a module receipt…" cta="+ Receipt" onAdd={(v) => props.onCreateReceipt?.(v, moduleKey)} />}
        </SectionCard>

        {/* Deferred Integrations */}
        <SectionCard title="Deferred Integrations" accent="#b8862a">
          <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11, color: CC_INK.muted, lineHeight: 1.7 }}>
            {isInherited && <li>Deep project-scoping of the underlying spine (per-project channels / roster / meetings) — later phase.</li>}
            {isInherited && catalog?.key === 'communications' && <li>Realtime messaging + presence — REALTIME_DEFERRED.</li>}
            {isFuture && <li>{label} backend — {catalog?.note ?? 'not built in v1'}.</li>}
            <li>External client access — deferred across the factory.</li>
          </ul>
        </SectionCard>

        {/* Next Steps */}
        <SectionCard title="Next Steps" accent="#059669">
          <p style={{ margin: 0, fontSize: 12, color: CC_INK.secondary, lineHeight: 1.5 }}>
            {!attached ? `Attach ${label} to this portal.`
              : isInherited ? `Open the ${label} surface to work the attached spine.`
              : isFuture ? `${label} is deferred — track it here once its backend lands.`
              : `Log module actions, notes, and receipts as work progresses.`}
          </p>
        </SectionCard>
      </div>
    </CommandCenterShell>
  )
}
